import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, RefreshCw, Inbox } from "lucide-react";
import { getSubmissions } from "@/lib/api";

const TABS = [
  ["referrals", "Referrals"],
  ["private_requests", "Private Requests"],
  ["ndis_requests", "NDIS Requests"],
  ["partnerships", "Partnership Enquiries"],
  ["threads_requests", "Threads Requests"],
  ["consultations", "Consultation EOIs"],
  ["contacts", "Messages"],
];

const label = (k) => k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const SubmissionCard = ({ item }) => (
  <div className="rounded-[28px] bg-creamdark border border-ochre/25 p-7">
    <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-terracotta mb-4">
      {item.created_at ? new Date(item.created_at).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" }) : ""}
    </p>
    <dl className="space-y-2.5">
      {Object.entries(item)
        .filter(([k, v]) => k !== "id" && k !== "created_at" && v !== null && v !== "" && v !== false)
        .map(([k, v]) => (
          <div key={k} className="grid sm:grid-cols-[200px_1fr] gap-1 sm:gap-4">
            <dt className="text-xs font-bold uppercase tracking-wider text-earth/60 pt-0.5">{label(k)}</dt>
            <dd className="text-sm text-charcoal leading-relaxed whitespace-pre-wrap">{v === true ? "Yes" : String(v)}</dd>
          </div>
        ))}
    </dl>
  </div>
);

const AdminDashboard = () => {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("referrals");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const token = localStorage.getItem("ngawilyi_admin_token");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getSubmissions(token);
      setData(data);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem("ngawilyi_admin_token");
        toast.error("Please sign in again.");
        navigate("/admin/login");
      } else {
        toast.error("Couldn't load submissions. Try refreshing.");
      }
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => {
    if (!token) {
      navigate("/admin/login");
      return;
    }
    load();
  }, [token, navigate, load]);

  const logout = () => {
    localStorage.removeItem("ngawilyi_admin_token");
    toast.success("Signed out.");
    navigate("/admin/login");
  };

  const items = data?.[tab] || [];

  return (
    <div data-testid="admin-dashboard" className="min-h-[80vh] px-6 pt-32 pb-24 bg-cream grain relative">
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-terracotta mb-3">Ngawilyi Admin</p>
            <h1 className="font-display font-light text-4xl sm:text-5xl text-charcoal">
              Submissions <span className="italic text-terracotta">in one place.</span>
            </h1>
          </div>
          <div className="flex gap-3">
            <button data-testid="admin-refresh-btn" onClick={load} className="inline-flex items-center gap-2 rounded-full border-2 border-charcoal px-6 py-3 text-xs font-bold uppercase tracking-widest text-charcoal transition-colors hover:bg-charcoal hover:text-cream">
              <RefreshCw className="h-4 w-4" /> Refresh
            </button>
            <button data-testid="admin-logout-btn" onClick={logout} className="inline-flex items-center gap-2 rounded-full bg-charcoal px-6 py-3 text-xs font-bold uppercase tracking-widest text-cream transition-colors hover:bg-terracotta">
              <LogOut className="h-4 w-4" /> Sign Out
            </button>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 mb-10" data-testid="admin-tabs">
          {TABS.map(([key, name]) => (
            <button
              key={key}
              data-testid={`admin-tab-${key}`}
              onClick={() => setTab(key)}
              className={`rounded-full px-6 py-3 text-xs font-bold uppercase tracking-widest transition-all duration-300 ${tab === key ? "bg-terracotta text-cream scale-105" : "bg-creamdark border border-ochre/30 text-earth hover:border-terracotta"}`}
            >
              {name} {data && <span className="ml-1 opacity-70">({data[key].length})</span>}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-earth/70 font-display italic text-2xl">Gathering the yarns…</p>
        ) : items.length === 0 ? (
          <div className="rounded-[36px] bg-blush/30 border border-blush p-14 text-center">
            <Inbox className="h-8 w-8 text-terracotta mx-auto mb-4" />
            <p className="font-display text-2xl text-charcoal">Nothing here yet</p>
            <p className="text-sm text-earth/70 mt-2">New {TABS.find(([k]) => k === tab)[1].toLowerCase()} will appear here as they come in.</p>
          </div>
        ) : (
          <div className="space-y-5" data-testid="admin-submissions-list">
            {items.map((item) => <SubmissionCard key={item.id} item={item} />)}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
